import {
  EuiAccordion,
  EuiButton,
  EuiButtonEmpty,
  EuiCodeBlock,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSpacer,
} from "@elastic/eui";
import StackTrace from "stacktrace-js";

import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { appInsights } from "../../AppInsights";
import { getLocalStorageWithExpiry, setLocalStorageWithExpiry } from "utils";

const ErrorFallback = ({ error, resetErrorBoundary }: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [pathname] = useState(location.pathname);
  const [mappedStack, setMappedStack] = useState<string>("");

  // Reset boundary if route changes
  useEffect(() => {
    if (location.pathname !== pathname) {
      resetErrorBoundary();
    }
  }, [location.pathname, pathname, resetErrorBoundary]);

  // Handle chunk load failure
  useEffect(() => {
    const chunkFailedMessage = /Loading.*chunk/;

    if (error?.message && chunkFailedMessage.test(error.message)) {
      if (!getLocalStorageWithExpiry("chunk_failed")) {
        setLocalStorageWithExpiry("chunk_failed", "true", 10000);

        appInsights?.trackTrace({
          message: "ErrorFallback - reloading initiated",
          severityLevel: 1,
          properties: { error: error?.message },
        });

        window.location.reload();
      }
    }
  }, [error]);

  // Map stack trace using source maps
  useEffect(() => {
    let isMounted = true;

    const mapStack = async () => {
      if (!error) return;

      try {
        const frames = await StackTrace.fromError(error);

        const formatted = frames
          .filter((frame) => frame.fileName)
          .map((frame) => {
            let file = frame.fileName || "unknown";
            try {
              const url = new URL(file);
              file = url.pathname;
            } catch {}
            const srcIndex = file.indexOf("/src/");
            if (srcIndex !== -1) {
              file = file.substring(srcIndex + 1);
            }

            const functionName = frame.functionName || "anonymous";

            return `at ${functionName} (${file}:${frame.lineNumber}:${frame.columnNumber})`;
          })
          .join("\n");

        if (isMounted) {
          setMappedStack(formatted);

          appInsights?.trackTrace({
            message: "ErrorFallback - Page Error encountered",
            severityLevel: 1,
            properties: {
              originalMessage: error?.message,
              mappedStack: formatted,
            },
          });
        }
      } catch (mappingError) {
        if (isMounted) {
          setMappedStack(error?.stack || error?.message);
        }
      }
    };

    mapStack();

    return () => {
      isMounted = false;
    };
  }, [error]);

  const err = (
    <EuiAccordion buttonContent="Detailed Error" className="txt-left" id="error-stack">
      <EuiPanel color="danger">
        <EuiCodeBlock color="danger" isCopyable>
          {mappedStack || error?.stack || error?.message}
        </EuiCodeBlock>
      </EuiPanel>
    </EuiAccordion>
  );

  return (
    <EuiEmptyPrompt
      style={{ backgroundColor: "var(--card-background)" }}
      body={<p>There was an error loading the screen. Contact your administrator for help.</p>}
      color="subdued"
      data-test-subj="errorOccured"
      footer={
        <>
          {err}

          <EuiSpacer />

          <EuiFlexGroup alignItems="center" justifyContent="spaceBetween">
            <EuiFlexItem grow={false}>
              <EuiButton
                iconType="refresh"
                onClick={() => {
                  dispatch({
                    type: globalFilterTypes.UPDATE_TOP_FILTER,
                    payload: null,
                  });
                  resetErrorBoundary();
                }}
                size="s">
                Refresh
              </EuiButton>
            </EuiFlexItem>

            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                iconType="home"
                onClick={() => {
                  dispatch({
                    type: globalFilterTypes.UPDATE_TOP_FILTER,
                    payload: null,
                  });
                  resetErrorBoundary();
                  navigate("/");
                }}>
                Go to Home Page
              </EuiButtonEmpty>
            </EuiFlexItem>
          </EuiFlexGroup>
        </>
      }
      iconType="warning"
      title={<h2>Error occurred</h2>}
    />
  );
};

export default ErrorFallback;
